// src/components/digital_twin/AiStormOutlook.js
// --- UPDATE: v1.0 — live AI weather outlook chip for the Digital Twin control bar
//   • GET /simulations/outlook (Open-Meteo storm diagnostics around the network)
//   • Refreshes every 15 min, only while the tab is visible; aborts on unmount
//   • Signal colour: none · advisory · watch · warning
//   • Click → run a live flood forecast (onRunForecast)
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge, Box, Button, HStack, Icon, Popover, PopoverArrow, PopoverBody, PopoverContent,
  PopoverTrigger, SimpleGrid, Spinner, Text, VStack, useColorModeValue,
} from '@chakra-ui/react';
import { FaCloudShowersHeavy, FaWind, FaBrain } from 'react-icons/fa';
import { getStormOutlook } from '../../services/simulationService';

const REFRESH_MS = 15 * 60 * 1000;
const SIGNAL = {
  none: { scheme: 'green', label: 'Calm' },
  advisory: { scheme: 'yellow', label: 'Advisory' },
  watch: { scheme: 'orange', label: 'Storm watch' },
  warning: { scheme: 'red', label: 'Storm warning' },
};

const Stat = ({ label, value }) => {
  const muted = useColorModeValue('gray.500', 'gray.400');
  return (
    <Box>
      <Text fontSize="9px" color={muted} textTransform="uppercase" fontWeight="800">{label}</Text>
      <Text fontSize="sm" fontWeight="900" fontFamily="mono">{value ?? '—'}</Text>
    </Box>
  );
};

const AiStormOutlook = ({ center, onRunForecast, onOutlook }) => {
  const [outlook, setOutlook] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const onOutlookRef = useRef(onOutlook);
  onOutlookRef.current = onOutlook;
  const lat = center?.lat, lng = center?.lng;

  const load = useCallback(async () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const data = await getStormOutlook({ lat, lng, hours: 72, signal: controller.signal });
      if (controller.signal.aborted) return;
      setOutlook(data);
      setError(null);
      onOutlookRef.current?.(data);
    } catch (err) {
      if (!err.cancelled) setError(err);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [lat, lng]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      abortRef.current?.abort();
    };
  }, [load]);

  const sig = SIGNAL[outlook?.storm_signal] || SIGNAL.none;
  const popBg = useColorModeValue('white', 'gray.800');

  if (error && !outlook) {
    return <Badge colorScheme="gray" variant="subtle" px={3} py={1} borderRadius="full" title={error.message}>WEATHER N/A</Badge>;
  }

  return (
    <Popover trigger="hover" placement="bottom-start" isLazy>
      <PopoverTrigger>
        <Box as="button" type="button" aria-label="AI storm outlook">
          <Badge colorScheme={sig.scheme} variant="solid" px={3} py={1} borderRadius="full" display="flex" alignItems="center" gap={1.5}>
            {loading && !outlook ? <Spinner size="xs" /> : <Icon as={FaBrain} boxSize={2.5} />}
            {outlook ? `${sig.label.toUpperCase()} · ${Math.round(outlook.peak?.wind_kts ?? 0)} KT · ${Math.round(outlook.totals?.rain_24h_mm ?? 0)} MM` : 'AI OUTLOOK'}
          </Badge>
        </Box>
      </PopoverTrigger>
      <PopoverContent bg={popBg} w="280px" borderRadius="xl" boxShadow="xl">
        <PopoverArrow bg={popBg} />
        <PopoverBody>
          {outlook ? (
            <VStack align="stretch" spacing={3}>
              <HStack justify="space-between">
                <HStack spacing={1.5}><Icon as={FaBrain} color="purple.400" /><Text fontWeight="800" fontSize="sm">72 h storm outlook</Text></HStack>
                <Badge colorScheme={sig.scheme}>{sig.label}</Badge>
              </HStack>
              <SimpleGrid columns={3} spacing={2}>
                <Stat label="Peak wind" value={`${outlook.peak?.wind_kts ?? '—'} kt`} />
                <Stat label="Gusts" value={`${outlook.peak?.gust_kts ?? '—'} kt`} />
                <Stat label="Min pres." value={outlook.peak?.min_pressure_hpa ?? '—'} />
                <Stat label="Rain 6h" value={`${outlook.totals?.rain_6h_mm ?? 0} mm`} />
                <Stat label="Rain 24h" value={`${outlook.totals?.rain_24h_mm ?? 0} mm`} />
                <Stat label="Rain 72h" value={`${outlook.totals?.rain_72h_mm ?? 0} mm`} />
              </SimpleGrid>
              <HStack fontSize="10px" spacing={3}>
                <HStack spacing={1}><Icon as={FaWind} /><Text>Est. surge {outlook.derived?.estimated_surge_m ?? 0} m</Text></HStack>
                {outlook.soil_saturation_index != null && <Text>Soil {Math.round(outlook.soil_saturation_index * 100)}%</Text>}
              </HStack>
              {onRunForecast && (
                <Button size="sm" colorScheme="cyan" leftIcon={<Icon as={FaCloudShowersHeavy} />} onClick={onRunForecast}>
                  Run flood forecast on twin
                </Button>
              )}
              <Text fontSize="9px" color="gray.500">Source: {outlook.source} · {new Date(outlook.retrieved_at).toLocaleTimeString()}</Text>
            </VStack>
          ) : (
            <HStack><Spinner size="sm" /><Text fontSize="sm">Loading outlook…</Text></HStack>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default memo(AiStormOutlook);
// --- END UPDATE